import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import useDebounce from "../../../Hooks/debounce";
import { Form } from "antd";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../Notifications/NotificationsUtils";
import { useQuery } from "react-query";
import { getAllFaculties } from "../../../Requests/faculty-requests";
import { getDepartmentsForFaculties } from "../../../Requests/department-requests";
import { getStudyProgramsForFaculties } from "../../../Requests/student-study-requests";
import { getCompanies } from "../../../Requests/company-requests";
import {
	toDomainModel,
	toFormModel,
} from "./transformers/announcement.transformers";
import {
	createAnnouncement,
	updateAnnouncement,
	getAnnouncementById,
} from "../../../Requests/announcement-requests";
import { useInitAnnouncementForm } from "./services/useInitAnnouncementForm";
import {
	AnnouncementFormController,
	AnnouncementFormProps,
} from "./AnnouncementForm.types";
import type { AnnouncementFormModel } from "./models/AnnouncementFormModel";

export const useAnnouncementFormController = (
	props: AnnouncementFormProps
): AnnouncementFormController => {
	const { t } = useTranslation();
	const [saveModalVisibility, setSaveModalVisibility] = useState(false);
	const [cancelModalVisibility, setCancelModalVisibility] = useState(false);
	const [spinning, setSpinning] = useState(false);
	const [facultySearch, setFacultySearch] = useState("");
	const [departmentSearch, setDepartmentSearch] = useState("");
	const [studyProgramSearch, setStudyProgramSearch] = useState("");
	const [companySearch, setCompanySearch] = useState("");
	const debouncedCompanySearch = useDebounce(companySearch, 500);
	const [facultyOptions, setFacultyOptions] = useState<React.ReactNode[]>([]);
	const [departmentOptions, setDepartmentOptions] = useState<React.ReactNode[]>(
		[]
	);
	const [studyProgramOptions, setStudyProgramOptions] = useState<
		React.ReactNode[]
	>([]);
	const [companyOptions, setCompanyOptions] = useState<React.ReactNode[]>([]);
	const [form] = Form.useForm<AnnouncementFormModel>();

	const recipientRoleValue = Form.useWatch("recipientRole", form);
	const facultiesValue = Form.useWatch("faculties", form);

	const { defaultValues } = useInitAnnouncementForm();

	const selectedFaculties = useMemo(() => {
		if (facultiesValue)
			return Array.isArray(facultiesValue) ? facultiesValue : [facultiesValue];
		return [];
	}, [facultiesValue]);

	const isMountAfterFetch = useRef(false);
	useEffect(() => {
		if (isMountAfterFetch.current) {
			isMountAfterFetch.current = false;
			return;
		}
		form.setFieldsValue({
			departments: [],
			studyPrograms: [],
			years: [],
		});
	}, [form, facultiesValue]);

	const openGetErrorNotification = () => {
		openNotification(
			t("announcements.error"),
			t("announcements.fetchError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const { data: facultiesList } = useQuery(
		["getFaculties"],
		() => getAllFaculties("", 1, 100),
		{
			onError: openGetErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const { data: departmentsList } = useQuery(
		["getFacultiesDepartments", selectedFaculties],
		() =>
			(selectedFaculties?.length ?? 0) === 0
				? Promise.resolve(null)
				: getDepartmentsForFaculties(selectedFaculties),
		{
			onError: openGetErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const { data: studyProgramsList } = useQuery(
		["getFacultiesStudyPrograms", selectedFaculties],
		() =>
			(selectedFaculties?.length ?? 0) === 0
				? Promise.resolve(null)
				: getStudyProgramsForFaculties(selectedFaculties),
		{
			onError: openGetErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const { data: companiesResponse, isLoading: isLoadingCompanies } = useQuery(
		["getCompaniesForAnnouncement", debouncedCompanySearch],
		() => getCompanies(debouncedCompanySearch || undefined, 1, 50),
		{
			onError: openGetErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const companiesList = companiesResponse?.data ?? null;

	const { data: announcement, isLoading: isLoadingAnnouncement } = useQuery(
		["getAnnouncement", props.announcementId],
		() =>
			props.announcementId
				? getAnnouncementById(props.announcementId)
				: Promise.resolve(null),
		{
			onError: openGetErrorNotification,
			onSuccess: (data) => {
				if (data && props.announcementId) {
					isMountAfterFetch.current = true;
					form.setFieldsValue(toFormModel(data) as any);
				}
			},
			enabled: !!props.announcementId,
			refetchOnWindowFocus: false,
		}
	);

	const isEditing = useMemo(
		() => !!props.announcementId && props.announcementId.length > 0,
		[props.announcementId]
	);

	const handleCancel = () => {
		props.setRedirect(true);
	};

	const onSuccess = () => {
		props.setRedirect(true);
		openNotification(
			t("announcements.title"),
			isEditing ? t("generic.success") : t("generic.success"),
			NOTIFICATION_TYPES.SUCCESS,
			3
		);
	};

	const handleSave = () => {
		form
			.validateFields()
			.then((state) => {
				const selectedFacultiesList =
					state.faculties && Array.isArray(state.faculties)
						? state.faculties
						: [];
				const dto = toDomainModel(state, selectedFacultiesList);
				setSpinning(true);
				(isEditing && props.announcementId
					? updateAnnouncement(props.announcementId, dto)
					: createAnnouncement(dto)
				)
					.then(onSuccess)
					.catch(() => {
						openNotification(
							t("announcements.error"),
							t("announcements.fetchError"),
							NOTIFICATION_TYPES.ERROR
						);
					})
					.finally(() => setSpinning(false));
			})
			.catch((err: { errorFields?: { errors: string[] }[] }) => {
				const firstError = err?.errorFields?.[0]?.errors?.[0];
				openNotification(
					t("announcements.form.save"),
					firstError ?? String(err),
					NOTIFICATION_TYPES.ERROR
				);
				setSaveModalVisibility(false);
			});
	};

	return {
		state: {
			form,
			recipientRoleValue,
			facultiesList: facultiesList ?? undefined,
			facultySearch,
			departmentsList: departmentsList ?? undefined,
			departmentSearch,
			studyProgramsList: studyProgramsList ?? undefined,
			studyProgramSearch,
			facultyOptions,
			departmentOptions,
			studyProgramOptions,
			companySearch,
			companyOptions,
			companiesList,
			spinning,
			cancelModalVisibility,
			saveModalVisibility,
		},
		actions: {
			setFacultyOptions,
			setDepartmentOptions,
			setStudyProgramOptions,
			setCompanyOptions,
			setFacultySearch,
			setDepartmentSearch,
			setStudyProgramSearch,
			setCompanySearch,
			setCancelModalVisibility,
			setSaveModalVisibility,
			handleCancel,
			handleSave,
		},
		computed: {
			isEditing,
			announcement: announcement ?? null,
			isLoadingCompanies,
			defaultValues,
		},
	};
};
